/*
  The permission keys the UI gates on.

  These mirror Wolfray.EventOS.Domain/Identity/Permissions.cs exactly. Hiding a
  control here is a courtesy to the user, never a security measure — the server
  checks the same key again on every call, and a hidden button that was reachable
  anyway would still be refused.
*/

export const P = {
  CommandView: "command.view",
  ExecutiveView: "command.executive",
  WorkView: "work.view",
  WorkCreate: "work.create",
  WorkEdit: "work.edit",
  WorkDelete: "work.delete",
  WorkAssign: "work.assign",
  WorkApprove: "work.approve",
  WorkComment: "work.comment",
  WorkInternalNotes: "work.internalnotes",
  WorkExport: "work.export",
  EventsView: "events.view",
  EventsCreate: "events.create",
  EventsEdit: "events.edit",
  EventsDelete: "events.delete",
  EventsPublish: "events.publish",
  EventsApprove: "events.approve",
  EventsExport: "events.export",
  EventsManageAccess: "events.manage",
  CrmView: "crm.view",
  CrmCreate: "crm.create",
  CrmEdit: "crm.edit",
  CrmDelete: "crm.delete",
  CrmApprove: "crm.approve",
  CrmExport: "crm.export",
  ClientsView: "clients.view",
  ClientsCreate: "clients.create",
  ClientsEdit: "clients.edit",
  ClientsDelete: "clients.delete",
  ClientsExport: "clients.export",
  PeopleView: "people.view",
  PeopleCreate: "people.create",
  PeopleEdit: "people.edit",
  PeopleDelete: "people.delete",
  PeopleExport: "people.export",
  PeopleSensitive: "people.sensitive",
  ProjectsView: "projects.view",
  ProjectsExport: "projects.export",
  ProjectsCreate: "projects.create",
  ProjectsEdit: "projects.edit",
  ProjectsApprove: "projects.approve",
  ProjectsDelete: "projects.delete",
  ScientificView: "scientific.view",
  ScientificCreate: "scientific.create",
  ScientificEdit: "scientific.edit",
  ScientificDelete: "scientific.delete",
  ScientificApprove: "scientific.approve",
  ScientificPublish: "scientific.publish",
  ScientificExport: "scientific.export",
  AbstractsView: "abstracts.view",
  AbstractsCreate: "abstracts.create",
  AbstractsEdit: "abstracts.edit",
  AbstractsReview: "abstracts.comment",
  AbstractsDecide: "abstracts.approve",
  AbstractsPublish: "abstracts.publish",
  AbstractsExport: "abstracts.export",
  RegistrationView: "registration.view",
  RegistrationCreate: "registration.create",
  RegistrationEdit: "registration.edit",
  RegistrationDelete: "registration.delete",
  RegistrationConfigure: "registration.configure",
  RegistrationPublish: "registration.publish",
  RegistrationExport: "registration.export",
  SponsorsView: "sponsors.view",
  SponsorsCreate: "sponsors.create",
  SponsorsEdit: "sponsors.edit",
  SponsorsDelete: "sponsors.delete",
  SponsorsApprove: "sponsors.approve",
  SponsorsExport: "sponsors.export",
  ExhibitorsExport: "exhibitors.export",
  ExhibitorsView: "exhibitors.view",
  ExhibitorsEdit: "exhibitors.edit",
  ExhibitorsCreate: "exhibitors.create",
  TravelView: "travel.view",
  TravelCreate: "travel.create",
  TravelEdit: "travel.edit",
  TravelDelete: "travel.delete",
  TravelExport: "travel.export",
  CreativeView: "creative.view",
  CreativeCreate: "creative.create",
  CreativeEdit: "creative.edit",
  CreativeApprove: "creative.approve",
  CreativePublish: "creative.publish",
  CreativeDelete: "creative.delete",
  ProductionView: "production.view",
  ProductionExport: "production.export",
  ProductionCreate: "production.create",
  ProductionEdit: "production.edit",
  ProductionApprove: "production.approve",
  ProductionDelete: "production.delete",
  VendorsView: "vendors.view",
  VendorsExport: "vendors.export",
  VendorsCreate: "vendors.create",
  VendorsEdit: "vendors.edit",
  VendorsApprove: "vendors.approve",
  VendorsDelete: "vendors.delete",
  FinanceView: "finance.view",
  FinanceCreate: "finance.create",
  FinanceEdit: "finance.edit",
  FinanceApprove: "finance.approve",
  FinanceDelete: "finance.delete",
  FinanceExport: "finance.export",
  FinanceMargins: "finance.manage",
  CommunicationsView: "communications.view",
  CommunicationsExport: "communications.export",
  CommunicationsCreate: "communications.create",
  CommunicationsEdit: "communications.edit",
  CommunicationsApprove: "communications.approve",
  CommunicationsSend: "communications.send",
  CommunicationsConfigure: "communications.configure",
  MarketingView: "marketing.view",
  MarketingEdit: "marketing.edit",
  MarketingCreate: "marketing.create",
  WebsiteView: "website.view",
  WebsiteCreate: "website.create",
  WebsiteEdit: "website.edit",
  WebsiteApprove: "website.approve",
  WebsitePublish: "website.publish",
  OnsiteView: "onsite.view",
  OnsiteCreate: "onsite.create",
  OnsiteEdit: "onsite.edit",
  OnsiteExport: "onsite.export",
  DocumentsView: "documents.view",
  DocumentsCreate: "documents.create",
  DocumentsEdit: "documents.edit",
  DocumentsDelete: "documents.delete",
  DocumentsApprove: "documents.approve",
  DocumentsExport: "documents.export",
  DocumentsRestricted: "documents.manage",
  AnalyticsView: "analytics.view",
  AnalyticsCreate: "analytics.create",
  AnalyticsExport: "analytics.export",
  AutomationsView: "automations.view",
  AutomationsCreate: "automations.create",
  AutomationsEdit: "automations.edit",
  AutomationsConfigure: "automations.configure",
  IntegrationsView: "integrations.view",
  IntegrationsConfigure: "integrations.configure",
  IntegrationsEdit: "integrations.edit",
  IntegrationsCreate: "integrations.create",
  AiView: "ai.view",
  AiUse: "ai.create",
  AiApprove: "ai.approve",
  AiConfigure: "ai.configure",
  AdminView: "administration.view",
  AdminUsers: "administration.manage",
  AdminCreate: "administration.create",
  AdminEdit: "administration.edit",
  AdminDelete: "administration.delete",
  AdminConfigure: "administration.configure",
  AdminExport: "administration.export",
  SaasView: "saas.view",
  SaasManage: "saas.manage",
  SaasConfigure: "saas.configure",
  PortalView: "portal.view",
  PortalEdit: "portal.edit",
  PortalComment: "portal.comment",
  PortalApprove: "portal.approve",
} as const;

export type PermissionKey = (typeof P)[keyof typeof P];
